import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconButton, Tab, Tabs, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PageHeader from '../components/PageHeader';
import TabHeader from '../components/TabHeader';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import type { ProjectAddress } from '../features/planning/types';
import type { SettingsProjectRow } from '../features/settings/types';
import ProjectRow from '../features/settings/components/projects/ProjectRow';
import ProjectModal from '../features/settings/components/projects/ProjectModal';
import AddressModal from '../features/settings/components/projects/AddressModal';
import { useManageProjects } from '../features/settings/hooks/useManageProjects';
import { useNotification } from '../context/useNotification';
import { PROJECTS_TAB_LABELS as LABELS } from '../utils/labels';
import { hasFullAccess } from '../utils/roles';
import type { MemberRole } from '../types/roles';

interface ProjectsPageProps {
  orgId: string | null;
  isSuperAdmin: boolean;
  role: MemberRole | null;
}

interface ProjectModalState {
  mode: 'create' | 'edit';
  project?: SettingsProjectRow;
}

interface AddressModalState {
  mode: 'create' | 'edit';
  projectId: string;
  address?: ProjectAddress;
}

export default function ProjectsPage({ orgId, isSuperAdmin, role }: ProjectsPageProps) {
  const navigate = useNavigate();
  const canEdit = hasFullAccess(role, isSuperAdmin);
  const {
    projects,
    archivedProjects,
    loadArchived,
    addProject,
    updateProject,
    archiveProject,
    unarchiveProject,
    deleteProject,
    addAddress,
    updateAddress,
    deleteAddress,
    setDefaultAddress,
  } = useManageProjects(orgId);
  const { notifySuccess, notifyError } = useNotification();

  const [tab, setTab] = useState<'active' | 'archived'>('active');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [projectModal, setProjectModal] = useState<ProjectModalState | null>(null);
  const [addressModal, setAddressModal] = useState<AddressModalState | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [pendingDeleteAddressId, setPendingDeleteAddressId] = useState<string | null>(null);

  const handleToggle = (id: string) =>
    setExpandedId((prev) => (prev === id ? null : id));

  const handleTabChange = (_: React.SyntheticEvent, value: 'active' | 'archived') => {
    setTab(value);
    setExpandedId(null);
    if (value === 'archived') loadArchived();
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteId) return;
    const id = pendingDeleteId;
    setPendingDeleteId(null);
    setDeleteError(null);
    const result = await deleteProject(id);
    if (result === 'ok') notifySuccess(LABELS.deleteSuccess);
    else if (result === 'has_machines') { setDeleteError(LABELS.deleteErrorHasMachines); notifyError(LABELS.deleteErrorHasMachines); }
    else { setDeleteError(LABELS.deleteError); notifyError(LABELS.deleteError); }
  };

  const handleConfirmDeleteAddress = async () => {
    if (!pendingDeleteAddressId) return;
    const id = pendingDeleteAddressId;
    setPendingDeleteAddressId(null);
    const ok = await deleteAddress(id);
    if (ok) notifySuccess(LABELS.addressDeleteSuccess); else notifyError(LABELS.mutationError);
  };

  const handleProjectSave = async (name: string, year: number): Promise<boolean> => {
    if (projectModal?.mode === 'create') {
      const result = await addProject(name, year);
      if (result === 'ok') { notifySuccess(LABELS.saveSuccess); return true; }
      if (result === 'limit_exceeded') { notifyError(LABELS.limitExceededError); return false; }
      notifyError(LABELS.mutationError);
      return false;
    }
    const ok = projectModal?.project ? await updateProject(projectModal.project.id, name, year) : false;
    if (ok) notifySuccess(LABELS.saveSuccess); else notifyError(LABELS.mutationError);
    return ok;
  };

  const handleAddressSave = async (
    name: string,
    street: string,
    houseNumber: string,
    postalCode: string,
    city: string,
  ): Promise<boolean> => {
    if (!addressModal) return false;
    const ok = addressModal.mode === 'create'
      ? await addAddress(addressModal.projectId, name, street, houseNumber, postalCode, city)
      : addressModal.address
        ? await updateAddress(addressModal.address.id, name, street, houseNumber, postalCode, city)
        : false;
    if (ok) notifySuccess(LABELS.addressSaveSuccess); else notifyError(LABELS.mutationError);
    return ok;
  };

  const handleArchive = async (id: string) => {
    const ok = await archiveProject(id);
    if (ok) notifySuccess(LABELS.archiveSuccess); else notifyError(LABELS.mutationError);
  };

  const handleUnarchive = async (id: string) => {
    const result = await unarchiveProject(id);
    if (result === 'ok') notifySuccess(LABELS.unarchiveSuccess);
    else if (result === 'limit_exceeded') notifyError(LABELS.limitExceededError);
    else notifyError(LABELS.mutationError);
  };

  const handleSetDefaultAddress = async (addressId: string, projectId: string) => {
    const ok = await setDefaultAddress(addressId, projectId);
    if (ok) notifySuccess(LABELS.addressDefaultSuccess); else notifyError(LABELS.mutationError);
  };

  const list = tab === 'active' ? projects : archivedProjects;

  return (
    <div className="h-dvh flex flex-col bg-background">
      <PageHeader />
      <main className="flex-1 overflow-y-auto p-4 md:p-8">
        <div className="max-w-xl mx-auto space-y-6">
          <TabHeader
            title={LABELS.title}
            description={LABELS.subtitle}
            addLabel={canEdit ? LABELS.newProjectButton : undefined}
            onAdd={canEdit ? () => setProjectModal({ mode: 'create' }) : undefined}
          />

          {deleteError && (
            <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-2 flex items-center gap-2">
              <p className="text-sm text-red-700 flex-1">{deleteError}</p>
              <Tooltip title={LABELS.dismissError}>
                <IconButton
                  size="small"
                  onClick={() => setDeleteError(null)}
                  aria-label={LABELS.dismissError}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
          )}

          <div className="rounded-2xl border border-border overflow-hidden bg-surface">
            <Tabs value={tab} onChange={handleTabChange} className="border-b border-border px-2">
              <Tab label={LABELS.tabActive} value="active" />
              <Tab label={LABELS.tabArchived} value="archived" />
            </Tabs>

            {list.length === 0 ? (
              <p className="text-sm text-text-secondary text-center py-6">
                {tab === 'active' ? LABELS.noActive : LABELS.noArchived}
              </p>
            ) : (
              list.map((p, i) => (
                <ProjectRow
                  key={p.id}
                  project={p}
                  showDivider={i > 0}
                  expanded={expandedId === p.id}
                  onToggle={() => handleToggle(p.id)}
                  onEdit={canEdit ? () => setProjectModal({ mode: 'edit', project: p }) : undefined}
                  onArchive={canEdit && !p.archived ? () => handleArchive(p.id) : undefined}
                  onUnarchive={canEdit && p.archived ? () => handleUnarchive(p.id) : undefined}
                  onDelete={canEdit ? () => setPendingDeleteId(p.id) : undefined}
                  onViewHistory={() => navigate('/project-history', { state: { projectId: p.id, projectName: p.name } })}
                  onAddAddress={canEdit ? () => setAddressModal({ mode: 'create', projectId: p.id }) : undefined}
                  onEditAddress={canEdit ? (address) =>
                    setAddressModal({ mode: 'edit', projectId: p.id, address }) : undefined}
                  onDeleteAddress={canEdit ? (id) => setPendingDeleteAddressId(id) : undefined}
                  onSetDefaultAddress={canEdit ? (id) => handleSetDefaultAddress(id, p.id) : undefined}
                />
              ))
            )}
          </div>

          {projectModal && (
            <ProjectModal
              project={projectModal.project}
              onClose={() => setProjectModal(null)}
              onSave={handleProjectSave}
            />
          )}
          {addressModal && (
            <AddressModal
              address={addressModal.address}
              onClose={() => setAddressModal(null)}
              onSave={handleAddressSave}
            />
          )}

          <ConfirmDeleteDialog
            open={!!pendingDeleteId}
            onClose={() => setPendingDeleteId(null)}
            onConfirm={handleConfirmDelete}
          />
          <ConfirmDeleteDialog
            open={!!pendingDeleteAddressId}
            onClose={() => setPendingDeleteAddressId(null)}
            onConfirm={handleConfirmDeleteAddress}
          />
        </div>
      </main>
    </div>
  );
}
